import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { login, verifyMfa, resendMfa, user, token, setError, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') || '/';
  const queryError = searchParams.get('error');

  useEffect(() => {
    if (queryError) {
      setLoginError(decodeURIComponent(queryError));
      setError(null);
    }
  }, [queryError, setError]);

  useEffect(() => {
    if (token && user) {
      navigate(from, { replace: true });
    }
  }, [token, user, navigate, from]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setSubmitting(true);
    try {
      const result = await login(emailOrUsername, password);
      if (result.status === 'mfa') {
        setMfaToken(result.mfaToken);
        setEmailHint(result.emailHint);
        setCode('');
        return;
      }
      navigate(from, { replace: true });
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setLoginError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setLoginError(null);
    setSubmitting(true);
    try {
      await verifyMfa(mfaToken, code.trim(), rememberDevice);
      navigate(from, { replace: true });
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Verification failed';
      setLoginError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!mfaToken) return;
    setLoginError(null);
    setResending(true);
    try {
      const hint = await resendMfa(mfaToken);
      if (hint) setEmailHint(hint);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Could not resend code';
      setLoginError(message);
      if (err.response?.status === 401) {
        setMfaToken(null);
        setCode('');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto">
        <div className="flex justify-center mb-6">
          <Mail className="h-12 w-12 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Sign in to EmailCraft</h2>
        <p className="text-gray-600 text-center mb-8">
          {mfaToken
            ? `Enter the 6-digit code sent to ${emailHint}.`
            : 'Sign in with your email or username and password.'}
        </p>

        <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
          {(loginError || error) && (
            <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
              {loginError || error}
            </div>
          )}

          {mfaToken ? (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Sign-in code
                </label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm tracking-widest text-center text-lg"
                  required
                  minLength={6}
                  maxLength={6}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Remember this browser for 30 days
              </label>
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {submitting ? 'Verifying...' : 'Verify and sign in'}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMfaToken(null);
                    setCode('');
                    setLoginError(null);
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="emailOrUsername" className="block text-sm font-medium text-gray-700 mb-1">
                  Email or username
                </label>
                <input
                  id="emailOrUsername"
                  type="text"
                  autoComplete="username"
                  value={emailOrUsername}
                  onChange={(e) => setEmailOrUsername(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {submitting ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
